/**
 * Grafana Faro Telemetry Module
 *
 * This module provides frontend observability for the React app using Grafana Faro.
 * It integrates with the existing backend OpenTelemetry setup by sending frontend
 * telemetry to an Alloy/Faro collector endpoint.
 *
 * Features:
 * - Automatic error tracking
 * - Console log capture
 * - Performance metrics (Web Vitals)
 * - Distributed tracing (W3C Trace Context propagation to backend)
 * - Session tracking
 * - Custom events and logs
 *
 * Configuration via environment variables:
 * - VITE_FARO_URL: Collector endpoint (required for telemetry to be enabled)
 * - VITE_FARO_APP_NAME: Application name (default: 'maja-react')
 * - VITE_FARO_APP_VERSION: Application version (default: '1.0.0')
 * - VITE_FARO_ENVIRONMENT: Environment name (default: 'development')
 *
 * @example
 * // Initialize in main.tsx (before React renders)
 * import { initializeFaro } from '@shared/telemetry';
 * initializeFaro();
 *
 * @example
 * // Log custom events
 * import { trackEvent, logInfo, logError } from '@shared/telemetry';
 * trackEvent('button_clicked', { button: 'submit', form: 'contact' });
 * logInfo('User completed onboarding');
 * logError(new Error('Payment failed'), { orderId: '123' });
 */

import {
  initializeFaro as initFaro,
  getWebInstrumentations,
  LogLevel,
  type Faro,
  type PushLogOptions,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

// Singleton Faro instance
let faro: Faro | null = null;

// Track initialization state for graceful handling
let initializationAttempted = false;
let initializationError: Error | null = null;

/**
 * Configuration for Faro initialization
 */
interface FaroConfig {
  url: string;
  appName: string;
  appVersion: string;
  environment: string;
}

/**
 * Get Faro configuration from environment variables with sensible defaults
 */
function getConfig(): FaroConfig | null {
  const url = import.meta.env.VITE_FARO_URL;

  // Telemetry is disabled if no URL is configured
  if (!url) {
    return null;
  }

  return {
    url,
    appName: import.meta.env.VITE_FARO_APP_NAME || 'maja-react',
    appVersion: import.meta.env.VITE_FARO_APP_VERSION || '1.0.0',
    environment: import.meta.env.VITE_FARO_ENVIRONMENT || 'development',
  };
}

/**
 * Get URL patterns for trace context propagation based on environment
 * These URLs will receive W3C Trace Context headers for distributed tracing
 *
 * IMPORTANT: Only propagate to Maja API services, NOT to Keycloak.
 * Keycloak (port 8443) doesn't allow the traceparent header in CORS.
 */
function getTracePropagationPatterns(): RegExp[] {
  const patterns: RegExp[] = [
    // Local development - Maja API services only (ports 5001-5010)
    // Excludes Keycloak (8443) which doesn't allow traceparent header
    /localhost:50(?:0[1-9]|10)\b/,
    /127\.0\.0\.1:50(?:0[1-9]|10)\b/,
  ];

  // Add production API domains if configured
  const apiDomain = import.meta.env.VITE_API_DOMAIN;
  if (apiDomain) {
    // Escape special regex characters and create pattern
    const escapedDomain = apiDomain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    patterns.push(new RegExp(escapedDomain));
  }

  return patterns;
}

/**
 * Initialize Grafana Faro for frontend observability.
 *
 * Call this once at application startup, before React renders.
 * Safe to call multiple times - will only initialize once.
 *
 * @returns The Faro instance if initialization succeeded, null otherwise
 */
export function initializeFaro(): Faro | null {
  // Prevent multiple initialization attempts
  if (initializationAttempted) {
    return faro;
  }
  initializationAttempted = true;

  const config = getConfig();

  // Telemetry disabled - not an error, just not configured
  if (!config) {
    if (import.meta.env.DEV) {
      console.info(
        '[Faro] Telemetry disabled - VITE_FARO_URL not configured. ' +
          'Set VITE_FARO_URL to enable frontend observability.'
      );
    }
    return null;
  }

  try {
    faro = initFaro({
      url: config.url,
      app: {
        name: config.appName,
        version: config.appVersion,
        environment: config.environment,
      },

      instrumentations: [
        // Standard web instrumentations
        ...getWebInstrumentations({
          // Capture console.log/warn/error
          captureConsole: true,
          // In production, only capture warn and error
          captureConsoleDisabledLevels:
            config.environment === 'production' ? [LogLevel.DEBUG, LogLevel.INFO, LogLevel.LOG] : [],
        }),

        // Distributed tracing - propagates trace context to backend
        // HTTP request events are filtered in beforeSend to reduce noise
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: getTracePropagationPatterns(),
          },
        }),
      ],

      // Session tracking for user journey analysis
      sessionTracking: {
        enabled: true,
        persistent: true, // Persist across page reloads
      },

      // Batch events before sending (reduces network overhead)
      batching: {
        enabled: true,
        sendTimeout: 2000, // Send after 2 seconds of inactivity
        itemLimit: 50, // Or when 50 items are collected
      },

      // Prevent app from crashing if collector is unavailable
      // Errors during send are logged but don't crash the app
      preventGlobalExposure: false,

      // Filter/transform events before sending
      beforeSend: (event) => {
        // Filter out noisy HTTP request tracing events
        // These duplicate what backend logs already capture
        if (event.type === 'event') {
          const payload = event.payload as { name?: string } | undefined;
          const eventName = payload?.name || '';
          if (
            eventName === 'faro.tracing.fetch' ||
            eventName === 'faro.tracing.xml-http-request'
          ) {
            return null; // Skip HTTP request events
          }
        }

        // Filter noisy console logs
        if (event.type === 'log') {
          const payload = event.payload as { message?: string } | undefined;
          const message = payload?.message || '';

          // Skip i18next loading messages
          if (message.includes('i18next::') || message.includes('i18next:')) {
            return null;
          }

          // Skip dev server noise (development only)
          if (config.environment === 'development') {
            if (
              message.includes('[HMR]') ||
              message.includes('[vite]') ||
              message.includes('Download the React DevTools')
            ) {
              return null;
            }
          }
        }
        return event;
      },
    });

    if (import.meta.env.DEV) {
      console.info('[Faro] Telemetry initialized', {
        appName: config.appName,
        appVersion: config.appVersion,
        environment: config.environment,
        collectorUrl: config.url,
      });
    }

    return faro;
  } catch (error) {
    // Store error but don't throw - app should continue working
    initializationError = error instanceof Error ? error : new Error(String(error));
    console.warn('[Faro] Failed to initialize telemetry (app will continue without it):', error);
    return null;
  }
}

/**
 * Get the Faro instance. Returns null if not initialized or initialization failed.
 */
export function getFaro(): Faro | null {
  return faro;
}

/**
 * Check if Faro telemetry is enabled and working
 */
export function isTelemetryEnabled(): boolean {
  return faro !== null;
}

/**
 * Get initialization error if any
 */
export function getInitializationError(): Error | null {
  return initializationError;
}

// =============================================================================
// Logging Utilities
// =============================================================================

type LogContext = Record<string, string | number | boolean | null | undefined>;

/**
 * Log an informational message
 *
 * @example
 * logInfo('User completed checkout', { orderId: '123', total: 99.99 });
 */
export function logInfo(message: string, context?: LogContext): void {
  if (!faro) return;
  const options: PushLogOptions = context ? { context: stringifyContext(context) } : {};
  faro.api.pushLog([message], { level: LogLevel.INFO, ...options });
}

/**
 * Log a warning message
 *
 * @example
 * logWarn('API response slow', { endpoint: '/api/users', duration: 3500 });
 */
export function logWarn(message: string, context?: LogContext): void {
  if (!faro) return;
  const options: PushLogOptions = context ? { context: stringifyContext(context) } : {};
  faro.api.pushLog([message], { level: LogLevel.WARN, ...options });
}

/**
 * Log a debug message (only captured in non-production environments)
 *
 * @example
 * logDebug('State updated', { previousState: 'loading', newState: 'ready' });
 */
export function logDebug(message: string, context?: LogContext): void {
  if (!faro) return;
  const options: PushLogOptions = context ? { context: stringifyContext(context) } : {};
  faro.api.pushLog([message], { level: LogLevel.DEBUG, ...options });
}

/**
 * Log an error with optional context
 *
 * @example
 * // Log an Error object
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   logError(error, { operation: 'riskyOperation', userId: user.id });
 * }
 *
 * // Log an error message
 * logError('Payment processing failed', { orderId: '123', reason: 'insufficient_funds' });
 */
export function logError(
  error: Error | string,
  context?: LogContext
): void {
  if (!faro) return;

  if (error instanceof Error) {
    faro.api.pushError(error, {
      context: context ? stringifyContext(context) : undefined,
    });
  } else {
    faro.api.pushLog([error], {
      level: LogLevel.ERROR,
      context: context ? stringifyContext(context) : undefined,
    });
  }
}

// =============================================================================
// Event Tracking
// =============================================================================

type EventAttributes = Record<string, string | number | boolean | null | undefined>;

/**
 * Track a custom event/action
 *
 * Use this for important user interactions and business events.
 *
 * @example
 * // Track button click
 * trackEvent('button_click', { button: 'add_to_cart', productId: '123' });
 *
 * // Track form submission
 * trackEvent('form_submit', { form: 'contact', success: true });
 *
 * // Track feature usage
 * trackEvent('feature_used', { feature: 'dark_mode', enabled: true });
 */
export function trackEvent(name: string, attributes?: EventAttributes): void {
  if (!faro) return;
  faro.api.pushEvent(name, attributes ? stringifyContext(attributes) : undefined);
}

/**
 * Track a page view manually
 *
 * Note: Faro automatically tracks page views via the web instrumentations.
 * Use this only if you need to track virtual page views or SPA navigation
 * that isn't automatically detected.
 *
 * @example
 * trackPageView('/products/123', { productName: 'Widget Pro' });
 */
export function trackPageView(url: string, attributes?: EventAttributes): void {
  if (!faro) return;
  faro.api.pushEvent('page_view', {
    url,
    ...stringifyContext(attributes || {}),
  });
}

// =============================================================================
// User Context
// =============================================================================

/**
 * Set the current user context for telemetry
 *
 * Call this after successful authentication to link telemetry data to the user.
 *
 * @example
 * // After login
 * setUser(user.id, user.email, user.name);
 *
 * // With additional attributes
 * setUser(user.id, user.email, user.name, { tenant: 'acme-corp', role: 'admin' });
 */
export function setUser(
  userId: string,
  email?: string,
  username?: string,
  attributes?: Record<string, string>
): void {
  if (!faro) return;
  faro.api.setUser({
    id: userId,
    email,
    username,
    attributes,
  });
}

/**
 * Clear user context (call on logout)
 *
 * @example
 * // On logout
 * clearUser();
 */
export function clearUser(): void {
  if (!faro) return;
  faro.api.resetUser();
}

// =============================================================================
// Session Context
// =============================================================================

/**
 * Set a session attribute
 *
 * Use this to add context that applies to the entire session.
 *
 * @example
 * setSessionAttribute('tenant', 'acme-corp');
 * setSessionAttribute('theme', 'dark');
 */
export function setSessionAttribute(key: string, value: string): void {
  if (!faro) return;
  faro.api.setSession({
    attributes: {
      [key]: value,
    },
  });
}

/**
 * Set multiple session attributes at once
 *
 * @example
 * setSessionAttributes({ tenant: 'acme-corp', locale: 'de-DE' });
 */
export function setSessionAttributes(attributes: Record<string, string>): void {
  if (!faro) return;
  faro.api.setSession({ attributes });
}

// =============================================================================
// View/Route Context
// =============================================================================

/**
 * Set the current view name (for SPA route tracking)
 *
 * This is automatically called by useRouteTracking hook.
 * Use manually only if you need custom view names.
 *
 * @example
 * setView('ProductDetail', { productId: '123' });
 */
export function setView(name: string, attributes?: Record<string, string>): void {
  if (!faro) return;
  faro.api.setView({ name });

  // Log view change as event with attributes if provided
  if (attributes && Object.keys(attributes).length > 0) {
    faro.api.pushEvent('view_change', { name, ...attributes });
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Convert mixed-type context to string values (Faro requires string values)
 */
function stringifyContext(context: LogContext | EventAttributes): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value !== null && value !== undefined) {
      result[key] = String(value);
    }
  }
  return result;
}

// =============================================================================
// Error Boundary Integration
// =============================================================================

/**
 * Report a React error boundary catch to Faro
 *
 * Call this from componentDidCatch in error boundaries.
 *
 * @example
 * componentDidCatch(error: Error, errorInfo: ErrorInfo) {
 *   reportReactError(error, errorInfo.componentStack);
 * }
 */
export function reportReactError(error: Error, componentStack?: string | null): void {
  if (!faro) return;

  faro.api.pushError(error, {
    context: {
      type: 'react_error_boundary',
      ...(componentStack && { componentStack }),
    },
  });
}
